import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls, OrthographicCamera, Text, Edges } from '@react-three/drei';
import './ModelViewer.css';

// STL Model component that loads and displays the STL file
const STLModel = ({ url }) => {
  const meshRef = useRef();
  const [geometry, setGeometry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { camera, gl } = useThree(); // Access the gl renderer

  useEffect(() => {
    if (!url) return;

    setLoading(true);
    setError(null);

    const loader = new STLLoader();
    loader.load(
      url,
      (loadedGeometry) => {
        setGeometry(loadedGeometry);
        setLoading(false);
      },
      (xhr) => {
        // Loading progress (not used for now)
      },
      (err) => {
        console.error('Error loading STL:', err);
        setError('Failed to load 3D model');
        setLoading(false);
      }
    );
  }, [url]);

  // useFrame(() => {
  //   if (meshRef.current) {
  //     // You can uncomment the following line if you wish to rotate via mesh
  //     // meshRef.current.rotation.y += 0.001;
  //   }
  // });

  // Set up the camera and scaling without recentering the model
  useEffect(() => {
    if (geometry && meshRef.current && camera) {
      geometry.computeBoundingBox();
      const boundingBox = geometry.boundingBox;
      const size = new THREE.Vector3();
      boundingBox.getSize(size);

      // Compute largest dimension without modifying the geometry position
      const maxDim = Math.max(size.x, size.y, size.z);

      // Calculate a uniform scale factor so that the model fits within view
      const scaleFactor = 2 / maxDim;
      meshRef.current.scale.set(scaleFactor, scaleFactor, scaleFactor);

      // Get the center for camera positioning without modifying the geometry
      const center = new THREE.Vector3();
      boundingBox.getCenter(center);

      // Adjust camera parameters for orthographic camera
      if (camera.isOrthographicCamera) {
        const aspect = gl.domElement.clientWidth / gl.domElement.clientHeight;
        let cameraSize = 100; // Adjust this value to control the initial zoom level

        camera.left = -cameraSize * aspect;
        camera.right = cameraSize * aspect;
        camera.top = cameraSize;
        camera.bottom = -cameraSize;

        // Position camera to look at the model's original position
        const cameraZ = cameraSize;
        camera.position.set(center.x, center.y, cameraZ);
        camera.lookAt(center.x, center.y, center.z);
      } else {
        // For perspective camera
        const fov = camera.fov * (Math.PI / 180);
        let cameraZ = Math.abs(maxDim / Math.sin(fov / 2));
        camera.position.z = cameraZ * 1.5;
        camera.lookAt(center.x, center.y, center.z);
      }

      camera.updateProjectionMatrix();
    }
  }, [geometry, camera, gl]);

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <mesh ref={meshRef}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="red" />
        <Text position={[0, 0, 0]} children={error} />
      </mesh>
    );
  }

  return geometry ? (
    <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]}>
      <primitive object={geometry} attach="geometry" />
      <meshStandardMaterial color="#D3D3D3" roughness={0.5} receiveShadow={true}/>
      <Edges color="#A9A9A9" />
    </mesh>
  ) : null;
};

// Main ModelViewer component
const ModelViewer = ({ stlFile, objectId, onDownload }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [autoRotate, setAutoRotate] = useState(true);
  const containerRef = useRef();
  const [stlUrl, setStlUrl] = useState('');

  useEffect(() => {
    if (stlFile) {
      if (stlFile.startsWith('http')) {
        setStlUrl(stlFile);
      } else {
        setStlUrl(stlFile);
      }
    }
  }, [stlFile]);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const toggleGrid = () => {
    setShowGrid(!showGrid);
  };

  const toggleAutoRotate = () => {
    setAutoRotate(!autoRotate);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div className={`model-viewer ${isFullScreen ? 'fullscreen' : ''}`} ref={containerRef}>
      {stlUrl ? (
        <>
          <Canvas>
            <ambientLight intensity={0.5} />
            <directionalLight
              position={[5, 5, 5]}
              intensity={0.2}
              castShadow
              shadow-mapSize-width={1024}
              shadow-mapSize-height={1024}
              shadow-bias={-0.001}
            />
            {/* <pointLight position={[10, 10, 10]} intensity={1} /> */}
            <OrthographicCamera makeDefault position={[0, 0, 5]} zoom={50} />
            <STLModel url={stlUrl} />
            <OrbitControls 
              enablePan={true} 
              enableZoom={true} 
              enableRotate={true} 
              autoRotate={autoRotate} 
              autoRotateSpeed={1} 
            />
            {showGrid && <gridHelper args={[10, 10, '#808080', '#505050']} />}
          </Canvas>
          
          <div className="control-buttons">
            <button 
              className="control-btn fullscreen-btn"
              onClick={toggleFullScreen}
              title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              <i className={`fas ${isFullScreen ? 'fa-compress' : 'fa-expand'}`}></i>
            </button>
            
            <button 
              className="control-btn download-btn"
              onClick={onDownload}
              title="Download model"
            >
              <i className="fas fa-download"></i>
            </button>

            <button 
              className="control-btn grid-btn"
              onClick={toggleGrid}
              title={showGrid ? "Hide gridlines" : "Show gridlines"}
            >
              <i className={`fas ${showGrid ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
            
            <button 
              className="control-btn rotate-btn"
              onClick={toggleAutoRotate}
              title={autoRotate ? "Stop rotation" : "Start rotation"}
            >
              <i className={`fas ${autoRotate ? 'fa-pause' : 'fa-play'}`}></i>
            </button>
          </div>
        </>
      ) : (
        <div className="model-placeholder">
          <p>3D Model Viewer</p>
          <p className="placeholder-hint">Model will appear here</p>
        </div>
      )}
    </div>
  );
};

export default ModelViewer;